import type { AppState } from "@auth0/auth0-react";
import { Auth0Provider } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { Outlet, useNavigate } from "react-router";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";
import AuthCheck from "./AuthCheck";
import { DevAuthBypassProvider } from "./DevAuthBypassProvider";
import SessionBootstrap from "./SessionBootstrap";

interface AuthProviderProps {
  children?: ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const config = WebAppConfig.getInstance();
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    const target = appState?.returnTo ?? "/dashboard";
    navigate(target, { replace: true });
  };

  if (!config.isAuthConfigured()) {
    return <DevAuthBypassProvider>{children ?? <Outlet />}</DevAuthBypassProvider>;
  }

  return (
    <Auth0Provider
      domain={config.auth0Domain}
      clientId={config.auth0ClientId}
      authorizationParams={{
        redirect_uri: config.auth0RedirectUri,
        audience: config.auth0Audience,
        scope: "openid profile email offline_access",
      }}
      useRefreshTokens
      useRefreshTokensFallback
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <AuthCheck>
        <SessionBootstrap>{children ?? <Outlet />}</SessionBootstrap>
      </AuthCheck>
    </Auth0Provider>
  );
};

export default AuthProvider;
